import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import StepDialog from "../components/StepDialog";
import ReadinessBadge from "../components/ReadinessBadge";
import AIPowerSearch from "../components/AIPowerSearch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  BookMarked,
  Plus,
  Search,
  Filter,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  Target,
  Calendar,
  User,
  FileText,
  Sparkles,
  ArrowRight,
  TrendingUp,
  FileWarning,
  Link2,
  Archive,
  ArchiveRestore
} from "lucide-react";
import { toast } from "sonner";

export default function LessonsLearnedPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const canArchive = ["bcm_manager", "admin"].includes(user?.role);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [aiSearchResults, setAiSearchResults] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailLesson, setDetailLesson] = useState(null);

  // AI Extract-CAPA state
  const [showAiExtractDialog, setShowAiExtractDialog] = useState(false);
  const [aiExtractInput, setAiExtractInput] = useState({
    exercise_type: "tabletop",
    scenario_tested: "",
    exercise_notes: "",
  });
  const [aiExtractResult, setAiExtractResult] = useState(null);
  const [aiExtracting, setAiExtracting] = useState(false);
  const [aiAutoSave, setAiAutoSave] = useState(false);

  const runAiExtractCapa = async () => {
    if (!aiExtractInput.exercise_notes || aiExtractInput.exercise_notes.length < 30) {
      toast.error("Paste at least a short paragraph of exercise notes");
      return;
    }
    setAiExtracting(true);
    try {
      const res = await api.post("/lessons-ai/extract-capa", {
        ...aiExtractInput,
        auto_save: aiAutoSave,
      });
      if (res.data?.success) {
        setAiExtractResult(res.data);
        const src = res.data.source === "gpt-4o" ? "AI" : "Fallback";
        const saved = res.data.saved ? ` · saved ${res.data.saved.lessons_ids.length} lessons + ${res.data.saved.capa_ids.length} CAPAs` : "";
        toast.success(`AI extracted ${res.data.data.findings.length} findings + ${res.data.data.capa_items.length} CAPAs (${src})${saved}`);
        if (aiAutoSave) fetchLessons();
      }
    } catch (e) {
      toast.error("AI extraction failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setAiExtracting(false);
    }
  };
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [lessonStep, setLessonStep] = useState(0);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [newLesson, setNewLesson] = useState({
    title: "",
    description: "",
    source_type: "exercise",
    source_id: "",
    category: "process",
    impact_level: "medium",
    recommendations: "",
    action_items: ""
  });

  useEffect(() => {
    fetchLessons();
  }, [showArchived]);

  const fetchLessons = async () => {
    try {
      setLoading(true);
      const response = await api.get("/lessons-learned", { params: { include_archived: showArchived } });
      if (response.data.success) {
        setLessons(response.data.data || []);
      } else {
        setLessons([]);
      }
      setLoadError(false);
    } catch (error) {
      // Honest failure: show nothing rather than fabricated sample data.
      setLessons([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveLesson = async (lesson, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await api.put(`/lessons-learned/${lesson.id}/archive`);
      if (res.data?.success) {
        toast.success("Lesson archived");
        setShowDetailDialog(false);
        fetchLessons();
      }
    } catch (error) {
      toast.error("Failed to archive lesson: " + (extractErrorMessage(error, "Unknown error")));
    }
  };

  const handleRestoreLesson = async (lesson, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await api.put(`/lessons-learned/${lesson.id}/restore`);
      if (res.data?.success) {
        toast.success("Lesson restored");
        setShowDetailDialog(false);
        fetchLessons();
      }
    } catch (error) {
      toast.error("Failed to restore lesson: " + (extractErrorMessage(error, "Unknown error")));
    }
  };

  const openDetail = (lesson) => {
    setDetailLesson(lesson);
    setShowDetailDialog(true);
  };

  const handleAddLesson = async () => {
    try {
      const response = await api.post("/lessons-learned", newLesson);
      if (response.data.success) {
        toast.success("Lesson learned added successfully");
        setShowAddDialog(false);
        setNewLesson({
          title: "",
          description: "",
          source_type: "exercise",
          source_id: "",
          category: "process",
          impact_level: "medium",
          recommendations: "",
          action_items: ""
        });
        fetchLessons();
      } else {
        toast.error("Failed to save lesson — it was not created. Please try again.");
      }
    } catch (error) {
      // Honest failure: the save did not happen. Keep the form so the user can retry.
      toast.error("Failed to save lesson — it was not created. Please try again.");
    }
  };

  // AI-powered lesson generation from incident description
  const handleAIGenerateLesson = async (incidentDescription) => {
    setAiGenerating(true);
    try {
      const response = await api.post("/ai/generate-lesson", {
        incident_description: incidentDescription
      });
      if (response.data.success) {
        setNewLesson(prev => ({
          ...prev,
          ...response.data.data
        }));
        toast.success("AI generated lesson details");
      }
    } catch (error) {
      // Honest failure: never fabricate AI recommendations. Populate nothing, report failure.
      toast.error("AI could not generate lesson details — nothing was filled in. Please try again or enter details manually.");
    } finally {
      setAiGenerating(false);
    }
  };

  // Convert lesson to PACA (Corrective/Preventive Action) - Using real backend
  const handleConvertToPACA = async (lesson) => {
    try {
      const response = await api.post(`/lessons-learned/${lesson.id}/convert-to-paca`, {
        lesson_id: lesson.id,
        paca_type: 'corrective'
      });
      if (response.data.success) {
        if (response.data.already_exists) {
          toast.info(response.data.data?.message || "A PACA already exists for this lesson.");
        } else {
          toast.success("PACA created successfully");
        }
        fetchLessons(); // Refresh to show updated status
        navigate("/capa");
      }
    } catch (error) {
      toast.error("Failed to create PACA: " + (extractErrorMessage(error, "Unknown error")));
    }
  };

  // Convert lesson to Improvement Action - Using real backend
  const handleConvertToImprovement = async (lesson) => {
    try {
      const response = await api.post(`/lessons-learned/${lesson.id}/convert-to-improvement`, {});
      if (response.data.success) {
        if (response.data.already_exists) {
          toast.info(response.data.data?.message || "An improvement action already exists for this lesson.");
        } else {
          toast.success("Improvement Action created successfully");
        }
        fetchLessons(); // Refresh to show updated status
        navigate("/improvement-actions");
      }
    } catch (error) {
      toast.error("Failed to create Improvement Action: " + (extractErrorMessage(error, "Unknown error")));
    }
  };

  const openConvertDialog = (lesson) => {
    setSelectedLesson(lesson);
    setShowConvertDialog(true);
  };

  const getImpactBadge = (level) => {
    const config = {
      critical: { className: "bg-red-500", label: "Critical" },
      high: { className: "bg-sky-700", label: "High" },
      medium: { className: "bg-amber-500", label: "Medium" },
      low: { className: "bg-blue-500", label: "Low" }
    };
    const cfg = config[String(level || "").toLowerCase()];
    if (!cfg) return <span className="text-slate-400 text-sm">—</span>;
    return <Badge className={cfg.className}>{cfg.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { variant: "destructive", icon: AlertTriangle, label: "Open" },
      in_progress: { variant: "default", className: "bg-blue-500", icon: RefreshCw, label: "In Progress" },
      action_taken: { variant: "default", className: "bg-indigo-500", icon: RefreshCw, label: "Action Taken" },
      in_review: { variant: "default", className: "bg-purple-500", icon: RefreshCw, label: "In Review" },
      closed: { variant: "default", className: "bg-emerald-500", icon: CheckCircle2, label: "Closed" }
    };
    const humanize = (s) => String(s || "unknown").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    const cfg = config[status] || { variant: "secondary", icon: AlertTriangle, label: humanize(status) };
    const Icon = cfg.icon;
    return (
      <Badge variant={cfg.variant} className={cfg.className}>
        <Icon className="w-3 h-3 mr-1" />
        {cfg.label}
      </Badge>
    );
  };

  const getCategoryIcon = (category) => {
    const icons = {
      process: Target,
      technical: Lightbulb,
      communication: FileText,
      documentation: BookMarked
    };
    const Icon = icons[category] || Target;
    return <Icon className="w-4 h-4" />;
  };

  const filteredLessons = (aiSearchResults || lessons).filter(lesson => {
    const matchesSearch = !searchTerm || lesson.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lesson.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSource = sourceFilter === "all" || lesson.source_type === sourceFilter;
    return matchesSearch && matchesSource;
  });

  const openLessons = lessons.filter(l => l.status === 'open').length;
  const actionTakenLessons = lessons.filter(l => l.status === 'action_taken').length;
  const inReviewLessons = lessons.filter(l => l.status === 'in_review').length;
  const closedLessons = lessons.filter(l => l.status === 'closed').length;
  const knownStatuses = ['open', 'action_taken', 'in_review', 'closed'];
  const otherLessons = lessons.filter(l => !knownStatuses.includes(l.status)).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lessons Learned</h1>
          <p className="text-slate-600">Capture and track insights from exercises, incidents, and audits</p>
          <div className="mt-2">
            {!loadError && (
              <ReadinessBadge
                total={lessons.length}
                complete={lessons.filter(l => l.status === 'closed').length}
                label="Lessons"
              />
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowAiExtractDialog(true)}
            className="gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
            data-testid="ai-extract-capa-btn"
          >
            <Sparkles className="w-4 h-4" />
            AI: Extract CAPA from Exercise Notes
          </Button>
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-lesson-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Lesson
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Lessons</p>
                <p className="text-2xl font-bold" data-testid="tile-total">{loadError ? '—' : lessons.length}</p>
              </div>
              <BookMarked className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Open</p>
                <p className="text-2xl font-bold text-red-600" data-testid="tile-open">{loadError ? '—' : openLessons}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Action Taken</p>
                <p className="text-2xl font-bold text-indigo-600" data-testid="tile-action-taken">{loadError ? '—' : actionTakenLessons}</p>
              </div>
              <RefreshCw className="w-8 h-8 text-indigo-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">In Review</p>
                <p className="text-2xl font-bold text-purple-600" data-testid="tile-in-review">{loadError ? '—' : inReviewLessons}</p>
              </div>
              <RefreshCw className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Closed</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="tile-closed">{loadError ? '—' : closedLessons}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        {otherLessons > 0 && (
          <Card className="border-l-4 border-l-slate-400">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Other</p>
                  <p className="text-2xl font-bold text-slate-600" data-testid="tile-other">{loadError ? '—' : otherLessons}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 space-y-3">
          <AIPowerSearch
            module="lessons_learned"
            onResults={(results) => setAiSearchResults(results)}
            onClear={() => setAiSearchResults(null)}
            placeholder='Try: "find lessons from exercises" or "cybersecurity incidents"...'
          />
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search lessons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="exercise">Exercises</SelectItem>
                <SelectItem value="incident">Incidents</SelectItem>
                <SelectItem value="audit">Audits</SelectItem>
                <SelectItem value="manual">Manual Entry</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchLessons}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            {canArchive && (
              <Button
                variant={showArchived ? "default" : "outline"}
                onClick={() => setShowArchived(v => !v)}
                data-testid="toggle-show-archived"
                className="whitespace-nowrap"
              >
                {showArchived ? "Hide Archived" : "Show Archived"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lessons List */}
      <Card>
        <CardHeader>
          <CardTitle>All Lessons Learned</CardTitle>
          <CardDescription>Click on a lesson to view details and action items</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : loadError ? (
            <div className="border border-red-200 bg-red-50 rounded-lg p-6 text-center" data-testid="lessons-load-error">
              <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-red-500" />
              <p className="font-semibold text-red-800">Lessons could not be loaded</p>
              <p className="text-sm text-red-700 mt-1">Nothing is being shown — this is not sample data. Your lessons register could not be retrieved.</p>
              <Button variant="outline" className="mt-4 border-red-300 text-red-700 hover:bg-red-100" onClick={fetchLessons} data-testid="lessons-retry-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
            </div>
          ) : filteredLessons.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="lessons-empty-state">
              <BookMarked className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No lessons learned found</p>
              <p className="text-sm">Add lessons from exercises, incidents, or manually</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredLessons.map((lesson) => (
                <div
                  key={lesson.id}
                  className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${lesson.is_archived ? 'bg-slate-50 border-slate-200 opacity-70' : ''}`}
                  data-testid={`lesson-${lesson.id}`}
                  onClick={() => openDetail(lesson)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-slate-100 rounded-lg">
                        {getCategoryIcon(lesson.category)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                          {lesson.title}
                          {lesson.is_archived && (
                            <Badge variant="secondary" className="bg-slate-200 text-slate-600" data-testid={`archived-badge-${lesson.id}`}>Archived</Badge>
                          )}
                        </h3>
                        <p className="text-sm text-slate-500">
                          Source: {lesson.source_name || lesson.source_type}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getImpactBadge(lesson.impact_level)}
                      {getStatusBadge(lesson.status)}
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm mb-3">{lesson.description}</p>
                  {lesson.recommendations && (
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-3">
                      <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" />
                        Recommendations
                      </p>
                      <p className="text-sm text-blue-700 mt-1">{lesson.recommendations}</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(lesson.created_at).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {lesson.created_by}
                      </span>
                    </div>
                    {/* Integration Actions */}
                    <div className="flex items-center gap-2">
                      {lesson.status !== 'closed' && !lesson.is_archived && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openConvertDialog(lesson);
                          }}
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          data-testid={`convert-lesson-${lesson.id}`}
                        >
                          <Link2 className="w-3 h-3 mr-1" />
                          Convert to Action
                        </Button>
                      )}
                      {canArchive && !lesson.is_archived && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleArchiveLesson(lesson, e)}
                          className="text-slate-500 hover:text-slate-700"
                          data-testid={`archive-lesson-${lesson.id}`}
                        >
                          <Archive className="w-3 h-3 mr-1" />
                          Archive
                        </Button>
                      )}
                      {canArchive && lesson.is_archived && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleRestoreLesson(lesson, e)}
                          className="text-emerald-600 hover:text-emerald-700"
                          data-testid={`restore-lesson-${lesson.id}`}
                        >
                          <ArchiveRestore className="w-3 h-3 mr-1" />
                          Restore
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lesson Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lesson-detail-dialog">
          {detailLesson && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4 pr-6">
                  <DialogTitle className="flex items-center gap-2 flex-wrap">
                    {detailLesson.title}
                    {detailLesson.is_archived && (
                      <Badge variant="secondary" className="bg-slate-200 text-slate-600">Archived</Badge>
                    )}
                  </DialogTitle>
                </div>
                <DialogDescription>
                  Source: {detailLesson.source_name || detailLesson.source_type} · Category: {detailLesson.category || "—"}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                {/* Badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  {getImpactBadge(detailLesson.impact_level)}
                  {getStatusBadge(detailLesson.status)}
                  {detailLesson.converted_to_paca && (
                    <Badge variant="outline" className="border-amber-300 text-amber-700">Converted to PACA</Badge>
                  )}
                  {detailLesson.converted_to_improvement && (
                    <Badge variant="outline" className="border-blue-300 text-blue-700">Converted to Improvement</Badge>
                  )}
                </div>

                {/* Description */}
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1">Description</p>
                  <p className="text-sm text-slate-600">{detailLesson.description || <span className="italic text-slate-400">No description provided.</span>}</p>
                </div>

                {/* Recommendations */}
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <p className="text-sm font-semibold text-blue-800 flex items-center gap-2 mb-1">
                    <Lightbulb className="w-4 h-4" /> Recommendations
                  </p>
                  <p className="text-sm text-blue-700 whitespace-pre-wrap" data-testid="detail-recommendations">
                    {detailLesson.recommendations || <span className="italic text-blue-400">No recommendations recorded.</span>}
                  </p>
                </div>

                {/* Action Items */}
                <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                  <p className="text-sm font-semibold text-purple-800 flex items-center gap-2 mb-1">
                    <Target className="w-4 h-4" /> Action Items
                  </p>
                  <p className="text-sm text-purple-700 whitespace-pre-wrap" data-testid="detail-action-items">
                    {detailLesson.action_items || <span className="italic text-purple-400">No action items recorded.</span>}
                  </p>
                </div>

                {/* Root cause & prevention (optional) */}
                {detailLesson.root_cause && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Root Cause</p>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{detailLesson.root_cause}</p>
                  </div>
                )}
                {detailLesson.prevention_measures && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Prevention Measures</p>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{detailLesson.prevention_measures}</p>
                  </div>
                )}

                {/* Meta grid */}
                <div className="grid grid-cols-2 gap-3 text-sm border-t border-slate-100 pt-3">
                  <div>
                    <p className="text-slate-400 text-xs">Impact Level</p>
                    <p className="text-slate-700 capitalize">{detailLesson.impact_level || "—"}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Status</p>
                    <p className="text-slate-700 capitalize">{String(detailLesson.status || "—").replace(/_/g, " ")}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Created</p>
                    <p className="text-slate-700">{detailLesson.created_at ? new Date(detailLesson.created_at).toLocaleString() : "—"} · {detailLesson.created_by || "—"}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Last Updated</p>
                    <p className="text-slate-700">{detailLesson.updated_at ? new Date(detailLesson.updated_at).toLocaleString() : "—"}</p>
                  </div>
                  {detailLesson.is_archived && detailLesson.archived_at && (
                    <div>
                      <p className="text-slate-400 text-xs">Archived</p>
                      <p className="text-slate-700">{new Date(detailLesson.archived_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>

                {/* Exercise source link */}
                {detailLesson.source_type === "exercise" && (
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <p className="text-sm text-amber-800 flex items-center gap-2">
                      <Link2 className="w-4 h-4" />
                      From exercise: <strong>{detailLesson.source_name || "Exercise"}</strong>
                    </p>
                    <Button
                      variant="link"
                      className="text-amber-700 h-auto p-0 mt-1 text-sm"
                      onClick={() => navigate("/exercises")}
                      data-testid="detail-exercise-link"
                    >
                      View Exercise Management <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2">
                {canArchive && !detailLesson.is_archived && (
                  <Button
                    variant="outline"
                    onClick={(e) => handleArchiveLesson(detailLesson, e)}
                    className="text-slate-600"
                    data-testid="detail-archive-btn"
                  >
                    <Archive className="w-4 h-4 mr-1" /> Archive
                  </Button>
                )}
                {canArchive && detailLesson.is_archived && (
                  <Button
                    variant="outline"
                    onClick={(e) => handleRestoreLesson(detailLesson, e)}
                    className="text-emerald-600 border-emerald-200"
                    data-testid="detail-restore-btn"
                  >
                    <ArchiveRestore className="w-4 h-4 mr-1" /> Restore
                  </Button>
                )}
                {detailLesson.status !== "closed" && !detailLesson.is_archived && (
                  <Button
                    onClick={() => { setShowDetailDialog(false); openConvertDialog(detailLesson); }}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="detail-convert-btn"
                  >
                    <Link2 className="w-4 h-4 mr-1" /> Convert to Action
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setShowDetailDialog(false)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Convert to PACA/Improvement Action Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Convert Lesson to Action</DialogTitle>
            <DialogDescription>
              Choose how to track this lesson's recommendations
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div
              className="border rounded-lg p-4 cursor-pointer hover:bg-amber-50 hover:border-amber-300 transition-colors"
              onClick={() => {
                handleConvertToPACA(selectedLesson);
                setShowConvertDialog(false);
              }}
              data-testid="convert-to-paca"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FileWarning className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-medium">Create PACA</h4>
                  <p className="text-sm text-slate-500">
                    For corrective or preventive actions requiring formal tracking
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
              </div>
            </div>
            <div
              className="border rounded-lg p-4 cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
              onClick={() => {
                handleConvertToImprovement(selectedLesson);
                setShowConvertDialog(false);
              }}
              data-testid="convert-to-improvement"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium">Create Improvement Action</h4>
                  <p className="text-sm text-slate-500">
                    For general improvements and enhancements
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Lesson Dialog — TurboTax Progressive Disclosure */}
      <StepDialog
        open={showAddDialog}
        onOpenChange={(open) => { setShowAddDialog(open); if (!open) setLessonStep(0); }}
        title="Add Lesson Learned"
        currentStep={lessonStep}
        onStepChange={setLessonStep}
        onSubmit={handleAddLesson}
        onCancel={() => setShowAddDialog(false)}
        submitLabel="Add Lesson"
        submitDisabled={!newLesson.title || !newLesson.description}
        canAdvance={
          lessonStep === 0 ? !!newLesson.title && !!newLesson.description :
          true
        }
        steps={[
          { id: "context", title: "Context", subtitle: "What was the lesson learned? Provide a title and description.", isComplete: !!newLesson.title && !!newLesson.description },
          { id: "classification", title: "Classification", subtitle: "Categorize the source, domain, and impact level of this lesson.", isComplete: !!newLesson.source_type && !!newLesson.category },
          { id: "actions", title: "Actions", subtitle: "What recommendations and follow-up actions should be taken?", isComplete: !!newLesson.recommendations || !!newLesson.action_items },
        ]}
      >
        {/* Step 0: Context */}
        {lessonStep === 0 && (
          <div className="space-y-4" data-testid="step-context">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Title *</Label>
              <Input
                value={newLesson.title}
                onChange={(e) => setNewLesson({...newLesson, title: e.target.value})}
                placeholder="Brief title for this lesson"
                data-testid="lesson-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Description *</Label>
              <Textarea
                value={newLesson.description}
                onChange={(e) => setNewLesson({...newLesson, description: e.target.value})}
                placeholder="Describe what happened and what was learned"
                rows={4}
                data-testid="lesson-description-input"
              />
            </div>
          </div>
        )}

        {/* Step 1: Classification */}
        {lessonStep === 1 && (
          <div className="space-y-4" data-testid="step-classification">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Source Type</Label>
              <div className="space-y-2">
                {[
                  { value: "exercise", label: "Exercise", desc: "From a planned drill or exercise" },
                  { value: "incident", label: "Incident", desc: "From a real disruption or incident" },
                  { value: "audit", label: "Audit", desc: "From an internal or external audit" },
                  { value: "manual", label: "Manual Entry", desc: "General observation or best practice" },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setNewLesson({...newLesson, source_type: opt.value})}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      newLesson.source_type === opt.value
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`source-${opt.value}`}
                  >
                    <p className="font-medium text-sm">{opt.label}</p>
                    <p className="text-xs text-slate-500">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Category</Label>
              <Select value={newLesson.category} onValueChange={(v) => setNewLesson({...newLesson, category: v})}>
                <SelectTrigger data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="process">Process</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="communication">Communication</SelectItem>
                  <SelectItem value="documentation">Documentation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Impact Level</Label>
              <div className="space-y-2">
                {[
                  { value: "critical", label: "Critical", color: "border-red-200 bg-red-50" },
                  { value: "high", label: "High", color: "border-orange-200 bg-orange-50" },
                  { value: "medium", label: "Medium", color: "border-amber-200 bg-amber-50" },
                  { value: "low", label: "Low", color: "border-green-200 bg-green-50" },
                ].map(level => (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() => setNewLesson({...newLesson, impact_level: level.value})}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      newLesson.impact_level === level.value
                        ? `${level.color} ring-1 ring-sky-200 border-sky-500`
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`impact-${level.value}`}
                  >
                    <p className="font-medium text-sm">{level.label}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Actions */}
        {lessonStep === 2 && (
          <div className="space-y-4" data-testid="step-actions">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Recommendations</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAIGenerateLesson(newLesson.description)}
                  disabled={!newLesson.description || aiGenerating}
                  className="text-purple-600"
                  data-testid="ai-generate-btn"
                >
                  {aiGenerating ? (
                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Sparkles className="w-3 h-3 mr-1" />
                  )}
                  AI Generate
                </Button>
              </div>
              <Textarea
                value={newLesson.recommendations}
                onChange={(e) => setNewLesson({...newLesson, recommendations: e.target.value})}
                placeholder="What should be done differently?"
                rows={3}
                data-testid="recommendations-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Action Items</Label>
              <Textarea
                value={newLesson.action_items}
                onChange={(e) => setNewLesson({...newLesson, action_items: e.target.value})}
                placeholder="List specific action items (one per line)"
                rows={3}
                data-testid="action-items-input"
              />
            </div>
          </div>
        )}
      </StepDialog>

      {/* AI Extract CAPA Dialog */}
      <Dialog open={showAiExtractDialog} onOpenChange={(v) => { setShowAiExtractDialog(v); if (!v) { setAiExtractResult(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ai-extract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI: Extract Findings &amp; CAPA from Exercise Debrief
            </DialogTitle>
            <DialogDescription>
              Paste the raw after-action debrief text — AI extracts structured findings, ISO 22301 clauses, and recommended CAPA items.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Exercise Type</Label>
                <Select value={aiExtractInput.exercise_type} onValueChange={(v) => setAiExtractInput(p => ({ ...p, exercise_type: v }))}>
                  <SelectTrigger data-testid="ai-extract-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tabletop">Tabletop</SelectItem>
                    <SelectItem value="functional">Functional</SelectItem>
                    <SelectItem value="full-scale">Full-scale</SelectItem>
                    <SelectItem value="walkthrough">Walkthrough</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Scenario Tested</Label>
                <Input placeholder="e.g., Ransomware attack on PAS"
                  value={aiExtractInput.scenario_tested}
                  onChange={(e) => setAiExtractInput(p => ({ ...p, scenario_tested: e.target.value }))}
                  data-testid="ai-extract-scenario"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Debrief Notes *</Label>
              <Textarea
                rows={8}
                placeholder="Paste the raw after-action report / debrief notes here. Include what worked, what failed, gaps, surprises, timing issues, etc."
                value={aiExtractInput.exercise_notes}
                onChange={(e) => setAiExtractInput(p => ({ ...p, exercise_notes: e.target.value }))}
                data-testid="ai-extract-notes"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={aiAutoSave}
                onChange={(e) => setAiAutoSave(e.target.checked)}
                data-testid="ai-extract-autosave"
              />
              Auto-save findings as Lessons Learned + CAPA items after extraction
            </label>

            {aiExtractResult && (
              <div className="space-y-3 pt-2 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <Badge className="bg-purple-100 text-purple-700 gap-1"><Sparkles className="w-3 h-3" /> {aiExtractResult.source === "gpt-4o" ? "AI" : "Fallback"}</Badge>
                  <span className="text-sm text-slate-700">
                    <strong>{aiExtractResult.data.findings.length}</strong> findings · <strong>{aiExtractResult.data.capa_items.length}</strong> CAPA items
                  </span>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 italic">
                  {aiExtractResult.data.overall_assessment}
                </div>
                <div>
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-600" /> Findings</h4>
                  <div className="space-y-2">
                    {aiExtractResult.data.findings.map((f, i) => (
                      <div key={i} className="rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-slate-900 text-sm">{f.title}</p>
                          <Badge variant="outline" className={`text-[10px] uppercase ${f.severity === "critical" ? "border-red-300 text-red-700 bg-red-50" : f.severity === "high" ? "border-amber-300 text-amber-700 bg-amber-50" : "border-slate-300 text-slate-600 bg-white"}`}>{f.severity}</Badge>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">{f.description}</p>
                        {f.iso_clause && <p className="text-[11px] text-purple-700 mt-1 font-mono">{f.iso_clause}</p>}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Target className="w-4 h-4 text-purple-600" /> Recommended CAPA Items</h4>
                  <div className="space-y-2">
                    {aiExtractResult.data.capa_items.map((c, i) => (
                      <div key={i} className="rounded-lg border border-purple-200 bg-purple-50 p-2.5">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="font-medium text-slate-900 text-sm">{c.title}</p>
                          <div className="flex gap-1.5">
                            <Badge variant="outline" className="text-[10px] uppercase bg-white">{c.type}</Badge>
                            <Badge variant="outline" className={`text-[10px] uppercase ${c.priority === "critical" ? "border-red-300 text-red-700 bg-red-50" : c.priority === "high" ? "border-amber-300 text-amber-700 bg-amber-50" : "border-slate-300 text-slate-600 bg-white"}`}>{c.priority}</Badge>
                          </div>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">{c.description}</p>
                        <p className="text-[11px] text-slate-500 mt-1">Owner: <strong>{c.responsible_role}</strong> · Target: {c.target_days}d · {c.iso_clause || ""}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {aiExtractResult.saved && (
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-sm text-emerald-800 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Saved {aiExtractResult.saved.lessons_ids.length} lesson(s) and {aiExtractResult.saved.capa_ids.length} CAPA item(s) to your library.
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAiExtractDialog(false)}>Close</Button>
            <Button
              onClick={runAiExtractCapa}
              disabled={aiExtracting || !aiExtractInput.exercise_notes}
              className="bg-purple-600 hover:bg-purple-700 gap-2"
              data-testid="ai-extract-run-btn"
            >
              {aiExtracting ? <>Extracting…</> : <><Sparkles className="w-4 h-4" /> Extract with AI</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
